import {
  CircleHelpIcon,
  CompassIcon,
  EyeIcon,
  HourglassIcon,
  RouteIcon,
  ShuffleIcon,
  TrendingUpIcon,
  WrenchIcon,
  type LucideIcon,
} from 'lucide-react'

export const CHECKOUT_URL = 'https://mega-pack-instalador-de-redes.impultienda.ar/checkout'

export const OFFER_ANCHOR = '#oferta'

export const SUPPORT_EMAIL = 'soportescmdigital@gmail.com'

export const PACK_IMAGE =
  'https://cdn.magicpatterns.com/uploads/21sYgGK6XM1VEFAhcNDzjY/Imagen_de_Codex_26_sept_2026,_23_18_05.png'

export const PRODUCT_NAME = 'MEGA PACK INSTALADOR DE REDES WiFi Y CABLEADO DESDE CERO'
export const PACK_IMAGE_ALT = 'MEGA PACK INSTALADOR DE REDES WiFi Y CABLEADO DESDE CERO: 6 tomos digitales y 5 bonos incluidos'
export const PRICE = '$19.990'
export const BONUS_TOTAL = '$39.950'

export const META_PIXEL_ID = '2226605141499138'

export interface IconItem {
  icon: LucideIcon
  title: string
  desc: string
}

/* 1. PROBLEMA */
export const problems: IconItem[] = [
  {
    icon: ShuffleIcon,
    title: 'INFORMACIÓN DISPERSA',
    desc: 'Guardas videos sobre routers, cable UTP, conectores RJ45, switches, WiFi, Access Points y direcciones IP, pero cada uno explica algo diferente y no sabes cómo conectar todo.',
  },
  {
    icon: CircleHelpIcon,
    title: 'NO SABES QUÉ APRENDER PRIMERO',
    desc: 'Routers, IP, DHCP, cableado, conectores, switches, WiFi, Mesh, PoE... hay tantos componentes y conceptos que empezar parece complicado.',
  },
  {
    icon: RouteIcon,
    title: 'TE FALTA UNA RUTA',
    desc: 'Quieres aprender una habilidad técnica que puedas convertir en un nuevo servicio, pero necesitas un camino que vaya desde los fundamentos hasta la instalación, configuración y diagnóstico.',
  },
  {
    icon: HourglassIcon,
    title: 'SIGUES POSTERGANDO',
    desc: 'Quieres comenzar, pero pasan las semanas y continúas buscando información en lugar de avanzar con un método organizado.',
  },
]

/* 2. SOLUCIÓN — 6 TOMOS */
export const tomos = [
  {
    title: 'FUNDAMENTOS DE REDES DESDE CERO',
    desc: 'Comprende cómo funcionan las redes, Internet y WiFi, y aprende conceptos fundamentales como LAN, WAN, Ethernet, IP, DHCP, DNS, Gateway, direcciones MAC, velocidad, latencia y diagnóstico básico.',
  },
  {
    title: 'CABLEADO UTP Y CONECTORES RJ45',
    desc: 'Aprende sobre cables Cat5e, Cat6 y Cat6A, conectores RJ45, normas T568A y T568B, crimpado, tester, Keystone, patch cords, patch panel, organización y detección de fallas.',
  },
  {
    title: 'ROUTERS Y CONFIGURACIÓN DE RED',
    desc: 'Comprende WAN, LAN, DHCP, rangos de direcciones, IP fija, reservas, DNS, seguridad, NAT, Port Forwarding, redes de invitados y configuración básica de routers.',
  },
  {
    title: 'WiFi, ACCESS POINTS Y SISTEMAS MESH',
    desc: 'Aprende cómo funciona el WiFi, bandas 2.4, 5 y 6 GHz, cobertura, interferencias, canales, Access Points, repetidores, Mesh, roaming y planificación para hogares y comercios.',
  },
  {
    title: 'SWITCHES, PoE Y REDES PARA CÁMARAS IP',
    desc: 'Comprende switches, Gigabit, PoE, PoE+, inyectores, presupuesto de potencia, cámaras IP, NVR, direccionamiento y cómo organizar redes destinadas a sistemas de videovigilancia.',
  },
  {
    title: 'DIAGNÓSTICO, INSTALACIÓN Y TRABAJO PROFESIONAL',
    desc: 'Aprende un método ordenado para detectar fallas, comprobar cableado y configuración, realizar relevamientos, calcular materiales, organizar instalaciones, preparar presupuestos y entregar trabajos profesionales.',
  },
]

/* 3. BONOS */
export const bonuses = [
  {
    title: 'GUÍA DE HERRAMIENTAS DEL INSTALADOR DE REDES',
    desc: 'Conoce las herramientas fundamentales para preparar cables, realizar conexiones, comprobar instalaciones, diagnosticar fallas y organizar trabajos de redes de forma profesional.',
    value: '$7.990',
  },
  {
    title: '20 FALLAS REALES DE REDES Y CÓMO DIAGNOSTICARLAS',
    desc: '20 situaciones prácticas con síntomas, posibles causas, pruebas y soluciones para aprender a diagnosticar problemas de WiFi, cableado, routers, switches, cámaras IP y PoE.',
    value: '$5.990',
  },
  {
    title: 'PLANTILLAS PROFESIONALES PARA INSTALACIONES Y CLIENTES',
    desc: 'Plantillas para relevamientos, materiales, puntos de red, direcciones IP, routers, switches, presupuestos, órdenes de trabajo, pruebas finales, mantenimiento y entrega profesional.',
    value: '$6.990',
  },
  {
    title: '10 PROYECTOS PRÁCTICOS DE REDES PASO A PASO',
    desc: 'Practica con proyectos reales: cables Ethernet, puntos de red, switches, WiFi, Access Points, Mesh, redes para comercios y sistemas con cámaras IP, PoE y NVR.',
    value: '$8.990',
  },
  {
    title: 'CÓMO CONSEGUIR TUS PRIMEROS CLIENTES COMO INSTALADOR DE REDES',
    desc: 'Aprende a definir tus servicios, atender consultas, realizar relevamientos, preparar presupuestos, presentar tu trabajo y dar los primeros pasos para conseguir clientes.',
    value: '$9.990',
  },
]

/* 4. BENEFICIOS */
export const benefits: IconItem[] = [
  {
    icon: CompassIcon,
    title: 'MENOS CONFUSIÓN',
    desc: 'Deja de saltar entre contenidos y sigue una ruta desde los fundamentos hasta el cableado, configuración, WiFi, instalación y diagnóstico.',
  },
  {
    icon: WrenchIcon,
    title: 'MÁS CONOCIMIENTO PRÁCTICO',
    desc: 'Comprende routers, cables UTP, conectores RJ45, switches, WiFi, Access Points, Mesh, PoE, cámaras IP y diagnóstico con explicaciones organizadas.',
  },
  {
    icon: EyeIcon,
    title: 'COMPRENDE LO QUE ESTÁS VIENDO',
    desc: 'Empieza a mirar una instalación y reconocer qué función cumplen el router, switch, cableado, Access Points y dispositivos conectados, y cómo se relacionan entre sí.',
  },
  {
    icon: TrendingUpIcon,
    title: 'UNA NUEVA HABILIDAD',
    desc: 'Construye conocimientos que pueden convertirse en el primer paso para incorporar un nuevo servicio técnico y ampliar tus posibilidades profesionales.',
  },
]

/* 5. TESTIMONIOS */
export const testimonials = [
  {
    quote:
      'Había visto muchos videos sobre routers, WiFi y cableado, pero seguía sin entender cómo se relacionaba todo. Con los tomos ordenados finalmente pude comprender desde las direcciones IP hasta cómo organizar una instalación.',
    author: 'Diego Fernández',
    meta: 'Técnico en formación, 29 años · Rosario, Argentina',
  },
  {
    quote:
      'Lo que más me ayudó fue entender que una buena red no es solamente conectar un router. Hay que pensar el cableado, la cobertura WiFi, los puntos de red, los switches y después comprobar que todo funcione correctamente.',
    author: 'Marcelo Ibáñez',
    meta: 'Instalador de seguridad, 41 años · Córdoba, Argentina',
  },
  {
    quote:
      'Trabajo con cámaras y quería entender mejor la parte de redes. El contenido de switches, PoE, IP y NVR me ayudó a conectar conceptos que antes aprendía por separado.',
    author: 'Laura Quiroga',
    meta: 'Técnica independiente, 35 años · Montevideo, Uruguay',
  },
  {
    quote:
      'Entré buscando aprender cableado y terminé entendiendo también diagnóstico. Ahora tengo mucho más claro qué revisar primero cuando un dispositivo no tiene Internet o una conexión empieza a fallar.',
    author: 'Cristian Molina',
    meta: 'Técnico independiente, 32 años · Santiago, Chile',
  },
  {
    quote:
      'Quería agregar instalaciones de redes a los servicios que ya realizaba. Las plantillas, proyectos y parte de presupuestos me dieron una estructura mucho más clara para organizar cada trabajo.',
    author: 'Héctor Ramírez',
    meta: 'Instalador, 48 años · Guadalajara, México',
  },
]

/* 6. FAQ */
export const faqs = [
  { q: '¿Es físico o digital?', a: 'Es un producto 100% digital.' },
  {
    q: '¿Necesito conocimientos previos?',
    a: 'No. El sistema comienza desde los fundamentos de redes, Internet y WiFi, y avanza paso a paso hacia cableado, configuración, instalación y diagnóstico.',
  },
  {
    q: '¿Qué voy a aprender?',
    a: 'Fundamentos de redes, cableado UTP y conectores RJ45, configuración de routers, WiFi, Access Points y Mesh, switches, PoE y redes para cámaras IP, y un método de diagnóstico e instalación profesional.',
  },
  {
    q: '¿Incluye ejercicios?',
    a: 'Sí. Incluye 10 proyectos prácticos paso a paso y 20 fallas reales con síntomas, pruebas y soluciones para practicar el diagnóstico.',
  },
  {
    q: '¿Los bonos tienen costo adicional?',
    a: 'No. Los 5 bonos están incluidos GRATIS con tu compra durante esta oferta.',
  },
  {
    q: '¿Es un pago único?',
    a: 'Sí. Realizas un único pago de $19.990. No hay mensualidades.',
  },
  {
    q: '¿Obtengo acceso inmediatamente?',
    a: 'Sí. Al completar tu compra recibirás acceso digital inmediato a los 6 tomos y los 5 bonos.',
  },
  {
    q: '¿Esto me habilita profesionalmente?',
    a: 'No. Es un producto educativo: no otorga títulos, licencias ni certificaciones. Te brinda conocimientos organizados que puedes complementar con práctica, formación y la normativa aplicable.',
  },
  {
    q: '¿Puedo aprender a mi ritmo?',
    a: 'Sí. Puedes avanzar por los 6 tomos y utilizar los bonos siguiendo tu propio ritmo, sin horarios ni fechas límite.',
  },
  {
    q: '¿Dónde solicito soporte?',
    a: 'Puedes escribirnos a soportescmdigital@gmail.com, el canal de soporte indicado al final de esta página.',
  },
]

/* POP-UP DE COMPRAS RECIENTES — ARGENTINA */
export const recentBuyers = [
  { name: 'Martín G.', city: 'Rosario, Santa Fe', minutes: 2 },
  { name: 'Lucas P.', city: 'Córdoba', minutes: 4 },
  { name: 'Sebastián R.', city: 'Mendoza', minutes: 6 },
  { name: 'Carla M.', city: 'La Plata, Buenos Aires', minutes: 9 },
  { name: 'Nicolás F.', city: 'San Miguel de Tucumán', minutes: 12 },
  { name: 'Federico A.', city: 'Mar del Plata', minutes: 15 },
  { name: 'Gustavo L.', city: 'Neuquén', minutes: 19 },
  { name: 'Romina S.', city: 'Salta', minutes: 23 },
  { name: 'Pablo D.', city: 'Bahía Blanca', minutes: 28 },
  { name: 'Javier T.', city: 'Santa Fe', minutes: 34 },
  { name: 'Matías V.', city: 'CABA, Buenos Aires', minutes: 39 },
  { name: 'Hernán C.', city: 'Posadas, Misiones', minutes: 45 },
]
